import { MATERIAL_LABEL, MATERIAL_SHORT, type MaterialKind } from "@/lib/requested-materials";
import { resolveMaterialHold } from "@/lib/agent-actions.functions";
import { startDealTermSheet } from "@/lib/term-sheet.functions";
import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AgentChatPanel } from "@/components/agent-chat-panel";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { handleAuthFailure } from "@/lib/auth-recovery";
import {
  listAgentActions,
  saveAgentActionDraft,
  markAgentActionSent,
  dismissAgentAction,
  getSignalItems,
  setAgentActionSendDirectly,
  type AgentActionRow,
} from "@/lib/agent-actions.functions";
import { sendAgentActionEmail } from "@/lib/email-send.functions";
import {
  MEETING_PAST_MESSAGE,
  POST_MEETING_SUBTYPE,
  pastMeetingConfirmation,
} from "@/lib/meeting-guard";
import { getMyGmailConnection } from "@/lib/google-connect.functions";
import { getOutlookConnection } from "@/lib/ms-connect.functions";
import { getAgentDartStatus } from "@/lib/agent-status.functions";
import {
  AgentStatusStrip,
  agentEmptyStateText,
} from "@/components/agent/agent-status-strip";
import { useOwnerTimezone } from "@/hooks/use-owner-timezone";
import { AgentActivityFeed } from "@/components/agent/agent-activity-feed";
import { AgentUpcomingMeetings } from "@/components/agent/agent-upcoming-meetings";
import { useOwnerMeetingProfile } from "@/hooks/use-owner-meeting-profile";
import { withMeetingInviteSentence } from "@/lib/meeting-draft";

type AgentSearch = { dealId?: string };

export const Route = createFileRoute("/_authed/agent")({
  validateSearch: (search: Record<string, unknown>): AgentSearch => {
    const raw = search["dealId"];
    return typeof raw === "string" && raw.trim() ? { dealId: raw } : {};
  },
  head: () => ({
    meta: [
      { title: "AgentDart | CapitalDart" },
      {
        name: "description",
        content:
          "Review contacts waiting on a reply, draft a follow-up, and mark it sent.",
      },
      { property: "og:title", content: "AgentDart | CapitalDart" },
      {
        property: "og:description",
        content:
          "Review contacts waiting on a reply, draft a follow-up, and mark it sent.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AgentQueuePage,
});


const REASON_LABELS: Record<string, string> = {
  interested_gone_quiet: "Interested investor gone quiet",
  drafted_not_sent: "Outreach drafted but never sent",
  stale_awaiting_response: "Awaiting response, no recent outreach",
  reply_received: "Investor replied",
  meeting_reminder: "Meeting tomorrow",
};

function reasonLabel(reason: string | null): string | null {
  if (!reason) return null;
  return REASON_LABELS[reason] ?? null;
}

/** The planner's own sentence for this item, with its draft guidance behind a disclosure. */
function PlannerReason({ action }: { action: AgentActionRow }) {
  const [open, setOpen] = useState(false);
  const reason = action.planner_reason?.trim() ?? "";
  const guidance = action.draft_guidance?.trim() ?? "";
  const rulesOnly = !reason || reason === "planner_capacity";
  if (!reason && !guidance) return null;
  return (
    <div className="mb-3 border-l-2 border-brand-gold/60 pl-3">
      <p className="text-[13px] leading-relaxed text-brand-muted">
        {rulesOnly ? "Rules used, planner unavailable." : reason}
      </p>
      {guidance && (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="mt-1 text-[13px] text-brand-ink underline underline-offset-4"
          >
            What it will say
          </button>
          {open && (
            <p className="mt-1 text-[13px] leading-relaxed text-brand-muted">
              {guidance}
            </p>
          )}
        </>
      )}
    </div>
  );
}


function AgentQueuePage() {
  const ownerTz = useOwnerTimezone();
  const fetchActions = useServerFn(listAgentActions);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["agent-actions"],
    queryFn: () => fetchActions({ data: {} }),
    staleTime: 15_000,
  });

  const fetchGmail = useServerFn(getMyGmailConnection);
  const gmail = useQuery({
    queryKey: ["gmail-connection"],
    queryFn: () => fetchGmail({}),
  });
  const fetchOutlook = useServerFn(getOutlookConnection);
  const outlook = useQuery({
    queryKey: ["outlook-connection"],
    queryFn: () => fetchOutlook({}),
  });
  const mailboxConnected =
    gmail.data?.connected === true || outlook.data?.connected === true;

  const fetchStatus = useServerFn(getAgentDartStatus);
  const statusQuery = useQuery({
    queryKey: ["agentdart-status", "account"],
    queryFn: () => fetchStatus({ data: {} }),
    staleTime: 30_000,
  });

  const grouped = useMemo(() => {
    const groups = new Map<string, { dealName: string; items: AgentActionRow[] }>();
    for (const item of query.data?.pending ?? []) {
      const g = groups.get(item.deal_id) ?? { dealName: item.deal_name, items: [] };
      g.items.push(item);
      groups.set(item.deal_id, g);
    }
    const list = Array.from(groups.entries()).map(([dealId, g]) => ({
      dealId,
      dealName: g.dealName,
      items: g.items.sort(
        (a, b) =>
          b.priority - a.priority || (b.days_stale ?? 0) - (a.days_stale ?? 0),
      ),
    }));
    list.sort(
      (a, b) =>
        (b.items[0]?.priority ?? 0) - (a.items[0]?.priority ?? 0) ||
        (b.items[0]?.days_stale ?? 0) - (a.items[0]?.days_stale ?? 0),
    );
    return list;
  }, [query.data]);

  // Opened from one deal: show only that deal, with a way back to everything.
  const { dealId: focusDealId } = Route.useSearch();
  const navigate = Route.useNavigate();
  const focusName = focusDealId
    ? (grouped.find((g) => g.dealId === focusDealId)?.dealName ??
      query.data?.sent?.find((s) => s.deal_id === focusDealId)?.deal_name ??
      null)
    : null;
  const visible = focusDealId
    ? grouped.filter((g) => g.dealId === focusDealId)
    : grouped;
  const otherCount = focusDealId
    ? grouped.reduce(
        (n, g) => (g.dealId === focusDealId ? n : n + g.items.length),
        0,
      )
    : 0;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["agent-actions"] });

  return (
    <main className="mx-auto w-full max-w-[640px] px-4 py-8 lg:max-w-[1100px]">
      <header className="mb-5">
        {focusDealId ? (
          <Link
            to="/deals/$dealId/agentdart"
            params={{ dealId: focusDealId }}
            search={{ section: "needs" }}
            className="text-[12.5px] text-brand-muted no-underline hover:text-brand-ink"
          >
            {focusName ? `← Back to ${focusName} AgentDart` : "← Back to the deal"}
          </Link>
        ) : (
          <Link
            to="/dashboard"
            search={{ all: "1" }}
            className="text-[12.5px] text-brand-muted no-underline hover:text-brand-ink"
          >
            ← Dashboard
          </Link>
        )}
        <div className="mt-2 grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
          <div className="min-w-0">
            <h1 className="font-serif text-2xl text-brand-ink">AgentDart</h1>
            <p className="mt-1 text-[14px] text-brand-muted">
              What AgentDart has been doing across your deals, and what it needs
              from you.
            </p>
          </div>
          <AgentChatPanel />
        </div>
      </header>

      <div className="mb-6">
        <AgentStatusStrip
          scope="account"
          status={statusQuery.data ?? null}
          loading={statusQuery.isLoading}
        />
      </div>

      {query.isLoading && (
        <p className="text-[14px] text-brand-muted">Loading follow-ups.</p>
      )}
      {query.error && (
        <p className="text-[14px] text-red-700">
          {query.error instanceof Error ? query.error.message : "Failed to load"}
        </p>
      )}
      {focusDealId && (
        <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-1">
          <p className="text-[14px] text-brand-muted">
            Showing {focusName ?? "this deal"} only.
          </p>
          <button
            type="button"
            onClick={() => navigate({ search: {} })}
            className="text-[14px] font-medium text-brand-ink underline underline-offset-4"
          >
            All deals{otherCount > 0 ? ` (${otherCount} more)` : ""}
          </button>
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-8">
          <section className="space-y-3">
            <h2 className="font-serif text-xl text-brand-ink">Needs you</h2>
            {!query.isLoading && visible.length === 0 && (
              <p className="text-[14px] leading-relaxed text-brand-muted">
                {agentEmptyStateText(statusQuery.data ?? null, "account", ownerTz)}
              </p>
            )}
            {visible.map((group) => (
              <section key={group.dealId} className="space-y-3">
                <h3 className="text-[13px] font-semibold uppercase tracking-wider text-brand-muted">
                  {group.dealName}
                </h3>
                {group.items.map((item) =>
                  item.action_type === "conference_match" ? (
                    <ConferenceCard key={item.id} action={item} onChanged={refresh} />
                  ) : (
                    <ActionCard
                      key={item.id}
                      action={item}
                      onChanged={refresh}
                      mailboxConnected={mailboxConnected}
                    />
                  ),
                )}
              </section>
            ))}
          </section>

          <AgentActivityFeed
            dealId={focusDealId ?? undefined}
            showDealName={!focusDealId}
          />
        </div>

        <div className="min-w-0 space-y-8">
          <AgentUpcomingMeetings
            dealId={focusDealId ?? undefined}
            showDealName={!focusDealId}
            collapsible
          />

          {(query.data?.sent?.length ?? 0) > 0 && (
            <section className="space-y-2">
              <h2 className="font-serif text-xl text-brand-ink">Recently sent</h2>
              {query.data?.sent?.map((s) => (
                <p key={s.id} className="text-[14px] text-brand-muted">
                  {s.contact_name}
                  {s.firm_name ? ` at ${s.firm_name}` : ""} on {s.deal_name}
                </p>
              ))}
            </section>
          )}
        </div>
      </div>
    </main>

  );
}

/** A conference worth a look. No draft, just the site and a way to say no. */
function ConferenceCard({
  action,
  onChanged,
}: {
  action: AgentActionRow;
  onChanged: () => void;
}) {
  const dismiss = useServerFn(dismissAgentAction);
  const [busy, setBusy] = useState(false);
  const site = action.conference_website
    ? /^https?:\/\//i.test(action.conference_website)
      ? action.conference_website
      : `https://${action.conference_website}`
    : null;

  async function notRelevant() {
    setBusy(true);
    try {
      await dismiss({ data: { actionId: action.id } });
      onChanged();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to dismiss");
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="space-y-3 rounded-xl border border-brand-line bg-brand-paper p-4">
      <div>
        <p className="text-[12px] font-medium uppercase tracking-wider text-brand-muted">
          {action.deal_name}
        </p>
        <p className="text-[15px] font-medium text-brand-ink">
          {action.draft_subject ?? "Conference"}
        </p>
        <p className="mt-1 text-[13px] text-brand-muted">{action.draft_body}</p>
        {action.conference_cost && (
          <p className="mt-1 text-[13px] text-brand-muted">{action.conference_cost}</p>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {site && (
          <Button type="button" size="sm" asChild>
            <a href={site} target="_blank" rel="noopener noreferrer">
              Open the site
            </a>
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => void notRelevant()}
        >
          Not relevant
        </Button>
      </div>
    </article>
  );
}

function ActionCard({
  action,
  onChanged,
  mailboxConnected,
}: {
  action: AgentActionRow;
  onChanged: () => void;
  mailboxConnected: boolean;
}) {
  const ownerTz = useOwnerTimezone();
  const ownerProfile = useOwnerMeetingProfile();
  const setSendDirectly = useServerFn(setAgentActionSendDirectly);
  const saveDraft = useServerFn(saveAgentActionDraft);
  const markSent = useServerFn(markAgentActionSent);
  const dismiss = useServerFn(dismissAgentAction);
  const fetchSignalItems = useServerFn(getSignalItems);
  const isSignal = action.action_type === "signal_touch";
  const isWarmIntro = action.action_type === "warm_intro_ask";
  // The weekly cadence drafts advice asks on the same action type.
  const isFounderAdvice =
    isWarmIntro &&
    ((action as { trigger?: { reason?: string } | null }).trigger?.reason ?? "") ===
      "founder_advice";
  const isLinkedInMessage = action.action_type === "linkedin_message";
  const isLinkedInPost = action.action_type === "linkedin_post";
  const isDraftOnly = isWarmIntro || isLinkedInMessage || isLinkedInPost;

  const [subject, setSubject] = useState(action.draft_subject ?? "");
  const [body, setBody] = useState(() =>
    withMeetingInviteSentence(
      action.draft_body ?? "",
      action,
      ownerProfile?.meeting_link_url,
      ownerProfile?.meeting_link_kind,
    ),
  );
  const [bodyAlt, setBodyAlt] = useState(action.draft_body_alt ?? "");
  const [signerTab, setSignerTab] = useState<"founder" | "banker">("founder");
  const [hasDraft, setHasDraft] = useState(Boolean(action.draft_body));
  const hasAlt =
    bodyAlt.trim().length > 0 && !isLinkedInMessage && !isLinkedInPost;
  const activeBody = hasAlt && signerTab === "banker" ? bodyAlt : body;
  const [sendDirectly, setSendDirectlyState] = useState(action.send_directly);
  const [generating, setGenerating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const sendEmail = useServerFn(sendAgentActionEmail);
  // A confirmation for a meeting that has already happened is held back until
  // the after-the-call note replaces it.
  const [afterCallDone, setAfterCallDone] = useState(
    action.subtype === POST_MEETING_SUBTYPE,
  );
  const meetingPassed =
    !afterCallDone &&
    pastMeetingConfirmation({
      trigger: {
        subtype: action.subtype,
        meeting_at: action.meeting_at,
        reason: action.reason,
      },
      next_step_kind: action.next_step_kind,
      next_step_at: action.next_step_at,
    });

  async function generate(opts?: { postMeeting?: boolean }) {
    const postMeeting = opts?.postMeeting === true;
    if (generating) return;
    setGenerating(true);
    setError(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) {
        // Session is gone: hand off to the shared recovery, which sends the
        // user to sign in and brings them back to this page.
        handleAuthFailure();
        return;
      }
      let signal:
        | {
            title?: string;
            summary?: string;
            source_name?: string;
            source_url?: string;
            date?: string;
            category?: string;
          }
        | undefined;
      if (isSignal && action.ai_action_id) {
        const res0 = await fetchSignalItems({
          data: { aiActionId: action.ai_action_id },
        });
        const top = res0.items[0];
        if (top) {
          signal = {
            title: top.title,
            summary: top.summary,
            source_name: top.source_name,
            source_url: top.source_url,
            date: top.date,
            category: top.category,
          };
        }
      }
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/bespoke-followup`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          dealId: action.deal_id,
          contactId: action.deal_contact_id,
          owner_tz: ownerTz,
          ...(signal ? { signal } : {}),
          ...(postMeeting ? { mode: "post_meeting" } : {}),
          ...(isLinkedInMessage ? { mode: "linkedin_message" } : {}),
          ...(isLinkedInPost
            ? { mode: "linkedin_post", post: { topic_hint: action.topic_hint } }
            : {}),
          ...(isWarmIntro
            ? {
                mode: "warm_intro_ask",
                warm_intro: {
                  founder_name: action.founder_name,
                  founder_title: action.founder_title,
                  founder_company: action.founder_company,
                  investor_firm: action.investor_firm,
                  investor_contact_name:
                    action.investor_contact_name ?? action.contact_name,
                  portfolio_deal_date: action.portfolio_deal_date,
                  portfolio_deal_stage: action.portfolio_deal_stage,
                  rationale: action.rationale,
                },
              }
            : {}),
        }),
      });
      const payload = (await res.json()) as {
        subject?: string;
        body?: string;
        body_alt?: string;
        error?: string;
      };
      if (!res.ok || payload.error) {
        throw new Error(payload.error || `Draft failed (${res.status})`);
      }
      if (typeof payload.subject !== "string" || typeof payload.body !== "string") {
        throw new Error("Malformed response");
      }
      const alt = typeof payload.body_alt === "string" ? payload.body_alt : "";
      const nextBody = withMeetingInviteSentence(payload.body, action, ownerProfile?.meeting_link_url, ownerProfile?.meeting_link_kind);
      await saveDraft({
        data: {
          actionId: action.id,
          subject: payload.subject,
          body: nextBody,
          bodyAlt: alt || null,
          ...(postMeeting ? { subtype: POST_MEETING_SUBTYPE as "post_meeting" } : {}),
        },
      });
      setSubject(payload.subject);
      setBody(nextBody);
      setBodyAlt(alt);
      setSignerTab("founder");
      setHasDraft(true);
      if (postMeeting) setAfterCallDone(true);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to draft");
    } finally {
      setGenerating(false);
    }
  }

  const [heldKinds, setHeldKinds] = useState<MaterialKind[]>(
    action.hold_reason === "material_missing"
      ? (action.material_missing_kinds as MaterialKind[])
      : [],
  );
  const materialHeld = heldKinds.length > 0;

  /** Drops the hold and drafts again without the promise. */
  async function sendWithoutMaterial() {
    if (busy || generating) return;
    setBusy(true);
    setError(null);
    try {
      await resolveMaterialHold({ data: { actionId: action.id, mode: "send_without" } });
      setHeldKinds([]);
      await generate();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to clear the hold");
    } finally {
      setBusy(false);
    }
  }

  /** Starts the Indicative Term Sheet, then looks again for the material. */
  async function generateTermSheet() {
    if (busy || generating) return;
    setBusy(true);
    setError(null);
    try {
      await startDealTermSheet({ data: { dealId: action.deal_id } });
      toast.success("Generating the Indicative Term Sheet. This item clears when it is ready.");
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to start the term sheet");
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    if (!action.contact_email || busy) return;
    if (materialHeld) {
      setError(`This deal has no ${MATERIAL_SHORT[heldKinds[0]!]} yet.`);
      return;
    }
    if (meetingPassed) {
      setError(MEETING_PAST_MESSAGE);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await saveDraft({
        data: { actionId: action.id, subject, body, bodyAlt: bodyAlt || null },
      });
      const res = await sendEmail({
        data: {
          actionId: action.id,
          subject,
          body: activeBody,
          ...(isWarmIntro ? { source: "agent_founder_advice" } : {}),
        },
      });
      if (!res.sent) {
        setError(
          res.reason === "not_connected"
            ? "Connect Gmail or Outlook in Profile, App Connections tab before sending."
            : res.reason === "no_email"
              ? "This contact has no email address."
              : res.reason === "meeting_in_past"
                ? MEETING_PAST_MESSAGE
                : res.reason === "material_missing"
                  ? "This deal does not have the material they asked for yet."
                  : "Save a subject and body before sending.",
        );
        return;
      }
      toast.success(
        res.redirected_to
          ? `Test mode: sent to ${res.redirected_to}`
          : `Sent from ${res.account_email}`,
      );
      if (!res.tracking_recorded) {
        setWarning("Sent, but reply tracking could not be recorded");
      }
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to send");
    } finally {
      setBusy(false);
    }
  }

  async function copyDraft() {
    try {
      await navigator.clipboard.writeText(`${subject}\n\n${activeBody}`);
      toast.success("Copied");
    } catch {
      setError("Could not copy");
    }
  }

  async function onMarkSent() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (subject.trim() && body.trim()) {
        await saveDraft({
          data: { actionId: action.id, subject, body, bodyAlt: bodyAlt || null },
        });
      }
      await markSent({ data: { actionId: action.id } });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update");
    } finally {
      setBusy(false);
    }
  }

  async function onDismiss() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await dismiss({ data: { actionId: action.id } });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to dismiss");
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="space-y-3 rounded-xl border border-brand-line bg-brand-paper p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="text-[12px] font-medium uppercase tracking-wider text-brand-muted">
            {action.deal_name}
          </p>
          {!isLinkedInPost && (
            <p className="text-[15px] font-medium text-brand-ink">
              {action.contact_name}
            </p>
          )}
          <p className="text-[13px] text-brand-muted">
            {action.firm_name ?? "Firm not listed"}
            {action.contact_email ? ` · ${action.contact_email}` : ""}
          </p>
        </div>
        {isLinkedInPost ? (
          <div className="max-w-[340px] text-right">
            <p className="text-[13px] font-medium text-brand-ink">LinkedIn post</p>
            {action.topic_hint && (
              <p className="text-[13px] text-brand-muted">{action.topic_hint}</p>
            )}
          </div>
        ) : isLinkedInMessage ? (
          <div className="max-w-[340px] text-right">
            <p className="text-[13px] font-medium text-brand-ink">
              LinkedIn message
            </p>
            {action.rationale && (
              <p className="text-[13px] text-brand-muted">{action.rationale}</p>
            )}
          </div>
        ) : isWarmIntro ? (
          <div className="max-w-[340px] text-right">
            <p className="text-[13px] font-medium text-brand-ink">
              {isFounderAdvice ? "Founder advice ask" : "Warm intro path"}
            </p>
            <p className="text-[13px] text-brand-muted">
              Via {action.founder_name ?? "a founder"},{" "}
              {action.founder_company ?? "their company"}, to{" "}
              {action.investor_contact_name ?? action.contact_name} at{" "}
              {action.investor_firm ?? action.firm_name ?? "the firm"}
            </p>
            {action.rationale && (
              <p className="text-[13px] text-brand-muted">{action.rationale}</p>
            )}
            {(action.founder_linkedin_url ?? action.founder_linkedin_search_url) && (
              <a
                href={
                  action.founder_linkedin_url ??
                  action.founder_linkedin_search_url ??
                  "#"
                }
                target="_blank"
                rel="noopener noreferrer"
                className="text-[13px] underline text-brand-ink"
              >
                Find on LinkedIn
              </a>
            )}
          </div>
        ) : isSignal ? (
          <div className="max-w-[320px] text-right">
            <p className="text-[13px] font-medium text-brand-ink">
              Fresh signal: {action.headline ?? "new item found"}
            </p>
            <p className="text-[13px] text-brand-muted">
              {[action.source_name, action.category].filter(Boolean).join(" · ")}
            </p>
            {action.source_url && (
              <a
                href={action.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[13px] underline text-brand-ink"
              >
                Read source
              </a>
            )}
          </div>
        ) : (
          <div className="text-right">
            {reasonLabel(action.reason) && (
              <p className="text-[13px] font-medium text-brand-ink">
                {reasonLabel(action.reason)}
              </p>
            )}
            <p className="text-[13px] text-brand-muted">
              {action.days_stale ?? 0} days
              {action.reason === "drafted_not_sent"
                ? " since the draft was prepared"
                : " since last outreach"}
            </p>
          </div>
        )}
      </div>

      <PlannerReason action={action} />



      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          onClick={() => void generate()}
          disabled={generating || busy}
        >
          {generating ? "Drafting" : hasDraft ? "Draft again" : "Generate draft"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => void onDismiss()}
          disabled={busy || generating}
        >
          Dismiss
        </Button>
      </div>

      {meetingPassed && (
        <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3">
          <p className="text-[13px] text-amber-900">{MEETING_PAST_MESSAGE}</p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() => void generate({ postMeeting: true })}
              disabled={generating || busy}
            >
              {generating ? "Working" : "Draft the after-call note"}
            </Button>
            {action.deal_contact_id && (
              <Button asChild type="button" size="sm" variant="outline">
                <Link
                  to="/deals/$dealId/investors/$contactId"
                  params={{
                    dealId: action.deal_id,
                    contactId: action.deal_contact_id,
                  }}
                >
                  Reschedule
                </Link>
              </Button>
            )}
          </div>
        </div>
      )}

      {materialHeld && (
        <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3">
          <p className="text-[13px] text-amber-900">
            {action.contact_name}
            {action.firm_name ? ` at ${action.firm_name}` : ""} asked for a{" "}
            {MATERIAL_SHORT[heldKinds[0]!]}. This deal has none yet.
          </p>
          <div className="flex flex-wrap gap-2">
            {heldKinds[0] === "term_sheet" ? (
              <Button
                type="button"
                size="sm"
                onClick={() => void generateTermSheet()}
                disabled={busy || generating}
              >
                Generate the {MATERIAL_LABEL[heldKinds[0]!]}
              </Button>
            ) : heldKinds[0] === "deck" || heldKinds[0] === "financial_model" ? (
              <Button asChild type="button" size="sm">
                <Link to="/deals/$dealId/refine" params={{ dealId: action.deal_id }}>
                  Upload it
                </Link>
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void sendWithoutMaterial()}
              disabled={busy || generating}
            >
              Send without it
            </Button>
          </div>
        </div>
      )}

      {hasDraft && (
        <div className="space-y-2">
          <div className="space-y-1">
            <label className="text-[12px] font-medium uppercase tracking-wider text-brand-muted">
              Subject
            </label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="space-y-1">
            <label className="text-[12px] font-medium uppercase tracking-wider text-brand-muted">
              Message
            </label>
            {hasAlt && (
              <div className="flex w-fit items-center gap-1 rounded-md border border-brand-line bg-brand-paper-2 p-0.5 text-[12px]">
                {(
                  [
                    ["founder", "From founder"],
                    ["banker", "From banker"],
                  ] as const
                ).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSignerTab(key)}
                    className={
                      signerTab === key
                        ? "rounded-sm bg-brand-paper px-2.5 py-1 text-brand-ink shadow-sm"
                        : "rounded-sm px-2.5 py-1 text-brand-muted hover:text-brand-ink"
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            {hasAlt && signerTab === "banker" ? (
              <Textarea
                value={bodyAlt}
                rows={10}
                onChange={(e) => setBodyAlt(e.target.value)}
              />
            ) : (
              <Textarea
                value={body}
                rows={10}
                onChange={(e) => setBody(e.target.value)}
              />
            )}
          </div>
          {isDraftOnly ? (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void copyDraft()}
                disabled={busy || !subject.trim() || !body.trim()}
              >
                Copy
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => void onMarkSent()}
                disabled={busy}
              >
                {busy ? "Working" : isLinkedInPost ? "Mark posted" : "Mark sent"}
              </Button>
              {isWarmIntro && action.contact_email && (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void send()}
                  disabled={busy || !subject.trim() || !body.trim()}
                >
                  {busy ? "Working" : "Send"}
                </Button>
              )}
            </div>
          ) : (
            <Button
              type="button"
              size="sm"
              onClick={() => void send()}
              disabled={busy || !action.contact_email || !subject.trim() || !body.trim()}
            >
              {busy ? "Working" : "Send"}
            </Button>
          )}
          {!isDraftOnly && action.deal_send_mode !== "direct" && (
            <label className="flex items-center gap-2 text-[13px] text-brand-ink">
              <input
                type="checkbox"
                checked={sendDirectly}
                disabled={!mailboxConnected}
                onChange={(e) => {
                  const next = e.target.checked;
                  setSendDirectlyState(next);
                  void setSendDirectly({
                    data: { actionId: action.id, sendDirectly: next },
                  }).catch(() => {
                    setSendDirectlyState(!next);
                  });
                }}
                className="h-3.5 w-3.5 accent-brand-accent disabled:opacity-50"
              />
              Send this one directly
            </label>
          )}
          {!isDraftOnly && !action.contact_email && (
            <p className="text-[13px] text-brand-muted">
              This contact has no email address on file.
            </p>
          )}
        </div>
      )}

      {error && <p className="text-[13px] text-red-700">{error}</p>}
      {warning && (
        <p className="text-[13px] text-amber-700">{warning}</p>
      )}
    </article>
  );
}
